/* eslint-disable @typescript-eslint/naming-convention */
import type { Reference } from '@ps/web-martech-cms-types';
import React from 'react';
import { useIntl } from 'react-intl';

import {
  CMSCTA,
  CMSContainer,
  CMSHTML,
  CMSHeading,
  CMSImage,
  CMSPageSection,
  PageBody
} from '@swf/cms';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import Translation from '@swf/components/Translation/Translation';
import { ResolverTag } from '@swf/core/enums';
import useResolver from '@swf/core/hooks/useResolver';

import claimYourWelcomeBonus from '../../assets/claim-your-welcome-bonus.png';
import createAnAccount from '../../assets/create_an_account.webp';
import download from '../../assets/download.jpg';

import {
  TXTCLI_Bonus,
  TXTCLI_Create_Account,
  TXTCLI_Download,
  TXTWCL_Claim_your_bonus
} from './NotFoundBody.messages.json';
import styles from './NotFoundBody.scss';

// TODO: https://jira.pyrsoftware.ca/jira/browse/WP-3064
const TXTWCL_Get_rewarded_for_making_your_f_ELL = {
  id: 'TXTWCL_Get_rewarded_for_making_your_f_ELL',
  defaultMessage: 'Get rewarded for making your first deposit'
};
const TXTWCL_Simple_registrationX_YouXll_be_ELL = {
  id: 'TXTWCL_Simple_registrationX_YouXll_be_ELL',
  defaultMessage: "Simple registration. You'll be playing in no time"
};
const TXTWCL_Create_an_account = {
  id: 'TXTWCL_Create_an_account',
  defaultMessage: 'Create an account'
};
const TXTWCL_Download_our_award_winning_and_ELL = {
  id: 'TXTWCL_Download_our_award_winning_and_ELL',
  defaultMessage: 'Download our award-winning and secure software'
};
const TXTWCL_Getting_started_is_easy = {
  id: 'TXTWCL_Getting_started_is_easy',
  defaultMessage: 'Getting started is easy'
};
const TXTWCL_Please_let_us_know_about_the_p_ELL = {
  id: 'TXTWCL_Please_let_us_know_about_the_p_ELL',
  defaultMessage:
    'Please let us know about the problem by visiting our <a>Help Center</a> and contacting us with the URL and/or the page from which you linked to get here.'
};
const TXTWCL_WeXre_sorryX_but_you_have_reac_ELL = {
  id: 'TXTWCL_WeXre_sorryX_but_you_have_reac_ELL',
  defaultMessage:
    "We're sorry, but you have reached a page that does not exist. This could be the result of a change in the web address or an incorrect URL."
};
const TXTWCL_Missing_Page = {
  id: 'TXTWCL_Missing_Page',
  defaultMessage: 'Missing Page'
};

const emptyReferences: Reference[] = [];

const NotFoundBody: React.FC = () => {
  const { formatMessage } = useIntl();
  const downloadUrl = useResolver(ResolverTag.PSDownload);

  return (
    <PageBody twoColumns={false} darkTheme={false} main={emptyReferences} side={emptyReferences}>
      <CMSContainer container={{ variant: 'bgStyle2', reference: [], visibility_rules: [] }}>
        <CMSPageSection
          heading={{
            level: 'h2',
            title: formatMessage(TXTWCL_Missing_Page),
            hero: false,
            alignment: 'center',
            size: null,
            target: null
          }}
          className={styles.center}
        >
          <CMSHTML tag="p" content={formatMessage(TXTWCL_WeXre_sorryX_but_you_have_reac_ELL)} />
          <CMSHTML tag="p" content="&nbsp;" />
          <Translation
            values={{
              a: (msg: string) => (
                <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
              )
            }}
            {...TXTWCL_Please_let_us_know_about_the_p_ELL}
          >
            {(message) => <p>{message}</p>}
          </Translation>
        </CMSPageSection>
      </CMSContainer>
      <CMSContainer container={{ variant: 'bgStyle1', reference: [], visibility_rules: [] }}>
        <CMSPageSection
          heading={{
            level: 'h2',
            title: formatMessage(TXTWCL_Getting_started_is_easy),
            hero: false,
            alignment: 'center',
            size: null,
            target: null
          }}
          columns={3}
          contain={true}
          grid={true}
          className={styles.center}
        >
          <article>
            <CMSImage
              asset={{
                url: download
              }}
              alt={formatMessage(TXTCLI_Download)}
              width={226}
              height={127}
              className={styles.thumbnail}
            />
            <CMSHeading
              level="h3"
              title={`1.${formatMessage(TXTCLI_Download)}`}
              alignment="center"
              size="m"
              target={null}
              hero={false}
            />
            <CMSHTML tag="p" content={formatMessage(TXTWCL_Download_our_award_winning_and_ELL)} />
          </article>
          <article>
            <CMSImage
              asset={{
                url: createAnAccount
              }}
              alt={formatMessage(TXTCLI_Create_Account)}
              width={226}
              height={127}
              className={styles.thumbnail}
            />
            <CMSHeading
              level="h3"
              title={`2.${formatMessage(TXTWCL_Create_an_account)}`}
              alignment="center"
              size="m"
              target={null}
              hero={false}
            />
            <CMSHTML tag="p" content={formatMessage(TXTWCL_Simple_registrationX_YouXll_be_ELL)} />
          </article>
          <article>
            <CMSImage
              asset={{
                url: claimYourWelcomeBonus
              }}
              alt={formatMessage(TXTCLI_Bonus)}
              width={226}
              height={127}
              className={styles.thumbnail}
            />
            <CMSHeading
              level="h3"
              title={`3.${formatMessage(TXTWCL_Claim_your_bonus)}`}
              alignment="center"
              size="m"
              target={null}
              hero={false}
            />
            <CMSHTML tag="p" content={formatMessage(TXTWCL_Get_rewarded_for_making_your_f_ELL)} />
          </article>
        </CMSPageSection>
        <CMSCTA
          hard_refresh={false}
          target="_self"
          variant="primary"
          url={downloadUrl}
          title={formatMessage(TXTCLI_Download)}
          track_id={undefined}
        />
      </CMSContainer>
    </PageBody>
  );
};

export default NotFoundBody;
